<template>
    <div class="congress-schedule-event-card">
        <!-- Collapsible Header -->
        <div class="congress-schedule-event-card-header" @click="toggleExpanded">
            <div class="congress-schedule-event-card-header-inner">
                <div class="congress-schedule-event-card-header-inner-left">
                    <icon
                        icon="phosphor/regular/arrows-out-cardinal"
                        class="congress-schedule-event-card-header-move cursor-grab"
                    ></icon>
                </div>
                <div class="congress-schedule-event-card-header-label">
                    <span class="font-medium">
                        {{ newsItem.title || lang('congresses::congresses.news.untitled') }}
                    </span>
                    <span
                        v-if="newsItem.is_published"
                        class="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                    >
                        {{ lang('congresses::congresses.news.published') }}
                    </span>
                    <span
                        v-else
                        class="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200"
                    >
                        {{ lang('congresses::congresses.news.draft') }}
                    </span>
                </div>
                <div class="congress-schedule-event-card-header-actions">
                    <Button type="button" size="sm" color="danger" variant="inline" @click.stop="$emit('remove')">
                        <span class="action-icon">
                            <Icon icon="phosphor/regular/trash"></Icon>
                        </span>
                        <span class="action-label">
                            {{ lang('congresses::congresses.news.remove') }}
                        </span>
                    </Button>
                    <icon
                        icon="hero/outline/chevron-down"
                        class="congress-schedule-event-card-header-actions-icon"
                        :class="{ 'rotate-180': expanded }"
                    ></icon>
                </div>
            </div>
        </div>

        <!-- Expanded Content -->
        <div v-show="expanded" class="event-content mt-2 space-y-2 px-5">
            <Field
                v-model="newsItem.title"
                size="sm"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.news.title'),
                    slug: `news_title_${newsItem._id}`,
                    type: 'text',
                    adminComponent: 'AdminText',
                    required: true,
                }"
            />

            <Field
                v-model="newsItem.content"
                label-position="top"
                :field="{
                    name: lang('congresses::congresses.news.content'),
                    slug: `news_content_${newsItem._id}`,
                    type: 'textarea',
                    adminComponent: 'AdminTextarea',
                }"
            />

            <div class="grid grid-cols-2 gap-5">
                <Field
                    v-model="newsItem.is_published"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.news.is_published'),
                        slug: `news_is_published_${newsItem._id}`,
                        type: 'checkbox',
                        adminComponent: 'AdminCheckbox',
                    }"
                />
                <Field
                    v-model="newsItem.published_at"
                    size="sm"
                    label-position="top"
                    :field="{
                        name: lang('congresses::congresses.news.published_at'),
                        slug: `news_published_at_${newsItem._id}`,
                        type: 'date',
                        adminComponent: 'AdminDate',
                        nullable: true,
                    }"
                >
                    <template #help>
                        {{ lang('congresses::congresses.news.published_at_help') }}
                    </template>
                </Field>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { lang } from '@/lang/composable';
import Button from '@src/Common/Components/Button.vue';
import Field from '@src/Common/Components/Field.vue';
import type { NewsItem } from '../types';

defineProps<{
    index: number;
}>();

defineEmits<{
    (e: 'remove'): void;
}>();

const newsItem = defineModel<NewsItem>();

const expanded = ref(false);

const toggleExpanded = () => {
    expanded.value = !expanded.value;
};
</script>
